import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "@/utils/auth";

import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { Menu, Popover } from "@/components/Base/Headless";
import Pagination from "@/components/Base/Pagination";
import { FormCheck, FormInput, FormSelect } from "@/components/Base/Form";
import Tippy from "@/components/Base/Tippy";
// import users from "@/fakers/users"; // Disabled faker profiles
import Button from "@/components/Base/Button";
import Table from "@/components/Base/Table";
import clsx from "clsx";
import _ from "lodash";
import { Edit } from "lucide-react";
import { useAppDispatch } from "@/stores/hooks";
import { setCompanyId } from "@/stores/sideMenuSlice";


type Company = {
    id: string;
    businessName: string;
    address: string;
    genericCoEmail: string;
    phoneNo: string;
    picName: string;
    picContact: string;
};

function ClientsPage() {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);

    const dispatch = useAppDispatch();

    useEffect(() => {
        (async () => {
            try {
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const res = await fetch(`${apiUrl}/companies`, {
                    headers: {
                        Authorization: `Bearer ${getToken()}`
                    }
                });
                const data = await res.json();
                setCompanies(Array.isArray(data?.data) ? data.data : []);
                setTotalPages(data.totalPages)

            } catch (err) {
                console.error("Failed to fetch companies:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, [currentPage]);

    const handleDelete = async (id: string) => {
        if (!confirm("Are you sure you want to delete this company?")) return;

        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const res = await fetch(`${apiUrl}/companies/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${getToken()}`
                }
            });

            if (res.ok) {
                // Remove deleted company from state
                setCompanies(prev => prev.filter(c => c.id !== id));
            } else {
                const error = await res.json();
                alert(`Failed to delete company: ${error?.error || "Unknown error"}`);
            }
        } catch (err) {
            console.error("Delete failed:", err);
            alert("Something went wrong deleting the company.");
        }
    };


    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                    <div className="col-span-12">
                        <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                List of Companies
                            </div>
                            <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                                <Link to="/add-company">
                                    <Button
                                        variant="primary"
                                        className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    >
                                        <Lucide icon="PenLine" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                        Add New Company
                                    </Button>
                                </Link>
                            </div>
                        </div>
                        <div className="flex flex-col gap-8 mt-3.5">
                            <div className="flex flex-col p-5 box box--stacked">
                                <div className="grid grid-cols-4 gap-5">
                                    <div className="col-span-4 md:col-span-2 xl:col-span-1 p-5 border border-dashed rounded-[0.6rem] border-slate-300/80 box shadow-sm">
                                        <div className="text-base text-slate-500">Number of Companies</div>
                                        <div className="mt-1.5 text-2xl font-medium">25</div>
                                        <div className="absolute inset-y-0 right-0 flex flex-col justify-center mr-5">
                                        </div>
                                    </div>
                                    <div className="col-span-4 md:col-span-2 xl:col-span-1 p-5 border border-dashed rounded-[0.6rem] border-slate-300/80 box shadow-sm">
                                        <div className="text-base text-slate-500">Number of Teams</div>
                                        <div className="mt-1.5 text-2xl font-medium">200</div>
                                        <div className="absolute inset-y-0 right-0 flex flex-col justify-center mr-5">
                                        </div>
                                    </div>
                                    <div className="col-span-4 md:col-span-2 xl:col-span-1 p-5 border border-dashed rounded-[0.6rem] border-slate-300/80 box shadow-sm">
                                        <div className="text-base text-slate-500">Number of Users</div>
                                        <div className="mt-1.5 text-2xl font-mediumm">1,500</div>
                                        <div className="absolute inset-y-0 right-0 flex flex-col justify-center mr-5">
                                        </div>
                                    </div>
                                    <div className="col-span-4 md:col-span-2 xl:col-span-1 p-5 border border-dashed rounded-[0.6rem] border-slate-300/80 box shadow-sm">
                                        <div className="text-base text-slate-500">Login Activity</div>
                                        <div className="mt-1.5 text-2xl font-mediumm">0</div>
                                        <div className="absolute inset-y-0 right-0 flex flex-col justify-center mr-5">                                            
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col box box--stacked">
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    <div>
                                        <div className="relative">
                                            <Lucide
                                                icon="Search"
                                                className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                            />
                                            <FormInput
                                                type="text"
                                                placeholder="Search users..."
                                                className="pl-9 sm:w-64 rounded-[0.5rem]"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 sm:ml-auto">
                                        <Menu>
                                            <Menu.Button
                                                as={Button}
                                                variant="outline-secondary"
                                                className="w-full sm:w-auto"
                                            >
                                                <Lucide
                                                    icon="Download"
                                                    className="stroke-[1.3] w-4 h-4 mr-2"
                                                />
                                                Export
                                                <Lucide
                                                    icon="ChevronDown"
                                                    className="stroke-[1.3] w-4 h-4 ml-2"
                                                />
                                            </Menu.Button>
                                            <Menu.Items className="w-40">
                                                <Menu.Item>
                                                    <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" />{" "}
                                                    PDF
                                                </Menu.Item>
                                                <Menu.Item>
                                                    <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" />
                                                    CSV
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Menu>
                                        <Popover className="inline-block">
                                            {({ close }) => (
                                                <>
                                                    <Popover.Button
                                                        as={Button}
                                                        variant="outline-secondary"
                                                        className="w-full sm:w-auto"
                                                    >
                                                        <Lucide
                                                            icon="ArrowDownWideNarrow"
                                                            className="stroke-[1.3] w-4 h-4 mr-2"
                                                        />
                                                        Filter
                                                        <div className="flex items-center justify-center h-5 px-1.5 ml-2 text-xs font-medium border rounded-full bg-slate-100 dark:bg-darkmode-400">
                                                            3
                                                        </div>
                                                    </Popover.Button>
                                                    <Popover.Panel placement="bottom-end">
                                                        <div className="p-2">
                                                            <div>
                                                                <div className="text-left text-slate-500">
                                                                    Position
                                                                </div>
                                                                <FormSelect className="flex-1 mt-2">
                                                                    <option value="">All Positions</option>
                                                                    {/* Faker profiles disabled - using empty filter */}
                                                                </FormSelect>
                                                            </div>
                                                            <div className="mt-3">
                                                                <div className="text-left text-slate-500">
                                                                    Department
                                                                </div>
                                                                <FormSelect className="flex-1 mt-2">
                                                                    <option value="">All Departments</option>
                                                                    {/* Faker profiles disabled - using empty filter */}
                                                                </FormSelect>
                                                            </div>
                                                            <div className="flex items-center mt-4">
                                                                <Button
                                                                    variant="secondary"
                                                                    onClick={() => {
                                                                        close();
                                                                    }}
                                                                    className="w-32 ml-auto"
                                                                >
                                                                    Close
                                                                </Button>
                                                                <Button variant="primary" className="w-32 ml-2">
                                                                    Apply
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Popover.Panel>
                                                </>
                                            )}
                                        </Popover>
                                    </div>
                                </div>
                                <div className="overflow-auto xl:overflow-visible">
                                    <Table className="border-b border-slate-200/60 text-xs">
                                        <Table.Thead className="text-xs">
                                            <Table.Tr>
                                                <Table.Td className="py-1.5">Business Name</Table.Td>
                                                <Table.Td className="py-1.5">Address</Table.Td>
                                                <Table.Td className="py-1.5">Generic Email</Table.Td>
                                                <Table.Td className="py-1.5">Phone No</Table.Td>
                                                <Table.Td className="py-1.5">PIC Name</Table.Td>
                                                <Table.Td className="py-1.5">PIC Contact</Table.Td>
                                                <Table.Td className="text-right py-1.5">Action</Table.Td>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {companies.map((company) => (
                                                <Table.Tr key={company.id} className="[&_td]:last:border-b-0">
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        <Link
                                                            to={`/projects?companyId=${company.id}`}
                                                            onClick={() => dispatch(setCompanyId(company.id))}
                                                            className="text-primary hover:underline"
                                                            title="View Projects"
                                                        >
                                                        {company.businessName}
                                                        </Link>
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {company.address}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {company.genericCoEmail || "-"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {company.phoneNo}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {company.picName}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {company.picContact}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 text-right border-dashed dark:bg-darkmode-600 text-xs">
                                                        <div className="flex items-center gap-3">
                                                            {/* Edit */}
                                                            <Link
                                                                to={`/companies/${company.id}/edit`}
                                                                className="btn btn-outline-secondary p-2 rounded-lg"
                                                                title="Edit"
                                                            >
                                                                <Lucide icon="Pencil" className="w-3 h-3" />
                                                            </Link>

                                                            {/* Delete (unchanged) */}
                                                            <button
                                                                onClick={() => handleDelete(company.id)}
                                                                className="btn btn-outline-danger p-2 rounded-lg"
                                                                title="Delete"
                                                            >
                                                                <Lucide icon="Trash2" className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>

                                    </Table>
                                </div>
                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        <Pagination.Link>
                                            <Lucide icon="ChevronsLeft" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link>
                                            <Lucide icon="ChevronLeft" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link>...</Pagination.Link>
                                        {Array.from({ length: totalPages }).map((_, i) => (
                                            <Pagination.Link
                                                key={i}
                                                active={currentPage === i + 1}
                                                onClick={() => setCurrentPage(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Link>
                                        ))}

                                        <Pagination.Link>
                                            <Lucide icon="ChevronRight" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link>
                                            <Lucide icon="ChevronsRight" className="w-3 h-3" />
                                        </Pagination.Link>
                                    </Pagination>
                                    <FormSelect className="sm:w-20 rounded-[0.5rem]">
                                        <option>10</option>
                                        <option>25</option>
                                        <option>35</option>
                                        <option>50</option>
                                    </FormSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>        
    );
}

export default ClientsPage;
